<template>
	<Layout>
		<ModalFollowSearch
			:modal-name="modalNameFollowSearch"
			@close="handleCloseFollowSearchModal"
		/>
		<template v-if="!isMobileViewport">
			<JumbotronCarousel
				v-if="slides.length > 0"
				:slides="slides"
			>
				<template #after>
					<HeaderSponsors
						v-if="headerSponsors"
						:sponsors="headerSponsors"
					/>
				</template>
			</JumbotronCarousel>
			<Jumbotron
				v-else
				:bg-image-url="bgImageUrl"
				:main-image-url="mainImageUrl"
				:title="title"
				:special-announcement="false"
			>
				<template #after>
					<HeaderSponsors
						v-if="headerSponsors"
						:sponsors="headerSponsors"
					/>
				</template>
			</Jumbotron>
		</template>
		<FeedNavigationDesktop />
		<div class="feed js-feed l-container l-container--full-width@mobile">
			<div class="l-row">
				<div
					class="l-col l-col--2@md feed__filter-container"
				>
					<PillFilter
						v-if="filterOptions.length > 1"
						class="feed__filter"
						:options="filterOptions"
						:value="filterValue"
						:multi="false"
						:orientation="isMobileViewport ? 'horizontal' : 'vertical'"
						@change="onFilterChange"
					/>
				</div>
				<div class="l-col l-col--8@md">
					<LoadingSpinner
						v-if="isLoading || !isInitialized"
						class="feed__loading-spinner"
					/>
					<FollowingEmptyState
						v-else-if="videos.items.length === 0 && isLoggedIn"
						:type="filterValue === FILTERS.MY_FEED ? FILTERS.MY_FEED : FILTERS.SAVED"
						@open-follow="openFollowSearchModal"
					/>
					<div
						class="feed__cards"
						:class="{
							'-loading': isLoading,
						}"
					>
						<template v-for="(event, i) in videos.items">
							<EventCard
								:ref="`eventCard-${event.id}`"
								:key="`${event.id}-${event.isBookmarked}`"
								v-bind="EventCardProps(event, {
									showEventTags: $arenaConfig.showEventCardTags,
									hidePaymentRestriction:	hasAccessToEvent(event),
									hideLoginRestriction:	isLoggedIn,
									hasBookmarkedEvent:	hasBookmarkedEvent(event),
									flexibleSize: true,
									isFeedCard: true,
									sharingEnabled: supportsNativeShare,
									isInViewport: activePreviewCardId === event.id.toString(),
									bookmarkEnabled: isLoggedIn,
								})"
								:data-event-id="event.id"
								@share="handleShare"
								@save="handleSave"
								@unsave="handleUnsave"
							/>
							<FollowingEmptyState
								v-if="i === 1 && filterValue === FILTERS.MY_FEED"
								:key="`following-empty-state-${i}`"
								class="feed__following-empty-state"
								:type="FILTERS.MY_FEED"
								@open-follow="openFollowSearchModal"
							/>
						</template>
					</div>
					<div
						v-if="videos.pagination.currentPage < videos.pagination.lastPage"
						ref="loadMoreTrigger"
						class="feed__load-more-trigger"
					/>
				</div>
			</div>
		</div>
	</Layout>
</template>

<script>
import Layout from '@js/Views/Layout';
import Jumbotron from '@js/components/Jumbotron';
import JumbotronCarousel from '@js/components/JumbotronCarousel';
import HeaderSponsors from '@js/components/HeaderSponsors';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import PillFilter from '@shared/components/PillFilter';
import EventCard from '@shared/components/EventCard/EventCard';
import ModalFollowSearch from '@js/Pages/FanAccount/ModalFollowSearch';
import FollowingEmptyState from '@js/Pages/FanAccount/partials/FollowingEmptyState';
import FeedNavigationDesktop from '@js/components/FeedNavigationDesktop';
import { EventCardProps } from '@shared/components/EventCard/EventCardProps';
import { AdType } from '@shared/enums/AdType';
import { mapGetters, mapActions } from 'vuex';
import { httpClient } from '@shared/js/api/httpClient';
import TrackingService from '@shared/js/TrackingService';
import { AnalyticsEvent } from '@shared/enums/AnalyticsEvent';
import storage from '@shared/js/storage';
import { actions as userActions } from '@shared/js/store/modules/user';
import laroute from '@laroute';

const FILTERS = {
	RECOMMENDED: 'recommended',
	SAVED:       'saved',
	MY_FEED:     'my_feed',
	ALL:         'all',
};

export default {
	name:       'ViewFeed',
	components: {
		Layout,
		Jumbotron,
		JumbotronCarousel,
		HeaderSponsors,
		LoadingSpinner,
		EventCard,
		PillFilter,
		FollowingEmptyState,
		ModalFollowSearch,
		FeedNavigationDesktop,
	},
	props: {
		bgImageUrl: {
			type:    String,
			default: null,
		},
		mainImageUrl: {
			type:    String,
			default: null,
		},
		title: {
			type:    String,
			default: null,
		},
		slides: {
			type:    Array,
			default: () => [],
		},
		ads: {
			type:    Array,
			default: () => [],
		},
		filter: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			EventCardProps,
			videos:                this.getInitialVideos(),
			isInitialized:         false,
			isLoading:             false,
			loadMoreObserver:      null,
			previewObserver:       null,
			filterValue:           null,
			modalNameFollowSearch: 'followSearchModal',
			activePreviewCardId:   null,
			FILTERS,
		};
	},
	computed: {
		...mapGetters({
			isMobileViewport:     'device/isMobileViewport',
			isTouchDevice:        'device/isTouchDevice',
			supportsNativeShare:  'device/supportsNativeShare',
			isAthlete:            'user/isAthlete',
			userId:               'user/userId',
			profileIsInitialized: 'user/isInitialized',
			hasAccessToEvent:     'user/hasAccessToEvent',
			hasBookmarkedEvent:   'user/hasBookmarkedEvent',
			isLoggedIn:           'user/isLoggedIn',
			followings:           'user/followings',
		}),
		headerSponsors() {
			return this.ads.find(ad => ad.type === AdType.SPONSORS_HEADER)?.logos;
		},
		filterOptions() {
			const options = [];

			if (this.isLoggedIn) {
				if (this.$arenaConfig.followingEnabled) {
					options.push({ value: FILTERS.MY_FEED });
				}
				options.push({ value: FILTERS.SAVED });
			}

			options.push({ value: FILTERS.RECOMMENDED });
			options.push({ value: FILTERS.ALL });

			return options.map((option) => {
				option.label = this.$I18n.trans('arena.feed.filter.' + option.value);
				return option;
			});
		},
	},
	watch: {
		async profileIsInitialized() {
			this.initialize();
		},
	},
	mounted() {
		// Add scroll listener to check if active video should stop playing
		if (this.isTouchDevice) {
			window.addEventListener('scroll', this.checkActiveVideoPosition);
		}

		if (!this.$arenaConfig?.loadArenaProfile) {
			this.initialize();
		}
	},
	beforeDestroy() {
		if (this.loadMoreObserver) {
			this.loadMoreObserver.disconnect();
		}
		if (this.preLoadObserver) {
			this.preLoadObserver.disconnect();
		}
		if (this.previewObserver) {
			this.previewObserver.disconnect();
		}
		// Remove scroll listener
		if (this.isTouchDevice) {
			window.removeEventListener('scroll', this.checkActiveVideoPosition);
		}
	},
	methods: {
		...mapActions({
			shareNative:     'device/shareNative',
			loadProfile:     `user/${userActions.LOAD_PROFILE}`,
			bookmarkEvent:   `user/${userActions.BOOKMARK_EVENT}`,
			unbookmarkEvent: `user/${userActions.UNBOOKMARK_EVENT}`,
		}),
		async initialize() {
			this.filterValue = this.filter || this.filterOptions[0].value;

			if (this.filterOptions?.length > 0) {
				await this.getVideos();
			}
			this.isInitialized = true;
		},
		getInitialVideos() {
			return {
				items:      [],
				pagination: {
					currentPage: 1,
					perPage:     10,
					lastPage:    1,
					total:       0,
				},
			};
		},
		async fetchVideos(page = 1, append = false) {
			if (this.isLoading) return;

			this.isLoading = true;
			try {
				const isPersonalized = [FILTERS.FOLLOWING, FILTERS.SAVED, FILTERS.MY_FEED].includes(this.filterValue);
				const urlSearchParams = new URLSearchParams();
				urlSearchParams.append('page', JSON.stringify(page));
				urlSearchParams.append('perPage', JSON.stringify(this.videos.pagination.perPage));
				urlSearchParams.append('sortOrder', JSON.stringify({ start: 'desc' }));

				// Cache buster for personalized feeds
				if (isPersonalized && storage.cacheTs) {
					urlSearchParams.append('t', storage.cacheTs);
				}

				if (this.filterValue === FILTERS.RECOMMENDED) {
					const related = {};

					if (this.followings?.length > 0) {
						related.teamIds = this.followings.filter(follow => follow.context?.name === 'team').map(follow => follow.contextableId);
						related.clubIds = this.followings.filter(follow => follow.context?.name === 'club').map(follow => follow.contextableId);
					}

					if (Object.keys(storage.visitedSports).length > 0) {
						const totalVisits = Object.values(storage.visitedSports).reduce((acc, curr) => acc + curr, 0);
						// Only include sports that have at least 10% of the total visits
						const sportsToInclude = Object.keys(storage.visitedSports).filter(sportId => {
							const visits = storage.visitedSports[sportId];
							return visits >= totalVisits * 0.1;
						});

						related.sportIds = sportsToInclude;
					}

					['teamIds', 'clubIds', 'sportIds'].forEach(key => {
						if (related[key]?.length > 0) {
							related[key] = related[key].sort((a, b) => a - b);
						}
						if (related[key]?.length === 0) {
							delete related[key];
						}
					});

					urlSearchParams.append('related', JSON.stringify(related));
				}

				const larouteParams = { type: this.filterValue };
				if (this.isLoggedIn) {
					larouteParams.userId = this.userId;
				}

				const response = await httpClient.getFeed(larouteParams, urlSearchParams);
				const data = response.data;

				// Store the last item's position before adding new items
				const lastItem = append ? this.videos.items[this.videos.items.length - 1] : null;
				const lastItemPosition = lastItem ? document.querySelector(`[data-event-id="${lastItem.id}"]`)?.getBoundingClientRect().top : null;

				if (append) {
					this.videos.items = [...this.videos.items, ...data.data];
				} else {
					this.videos.items = data.data;
				}
				this.videos.pagination = data.meta;

				// Restore scroll position after new items are added
				if (append && lastItemPosition !== null) {
					this.$nextTick(() => {
						const newLastItem = document.querySelector(`[data-event-id="${lastItem.id}"]`);
						if (newLastItem) {
							const newPosition = newLastItem.getBoundingClientRect().top;
							window.scrollBy(0, newPosition - lastItemPosition);
						}
					});
				}

				// Set up observers after initial load or after appending new items
				this.$nextTick(() => {
					this.setupIntersectionObserver();
					this.setupPreviewObserver();
					this.setupPreloadObserver();
				});
			} catch (error) {
				// show empty feed
				this.videos = this.getInitialVideos();
			} finally {
				this.isLoading = false;
			}
		},
		setupIntersectionObserver() {
			// Clean up existing observer if it exists
			if (this.loadMoreObserver) {
				this.loadMoreObserver.disconnect();
			}

			this.loadMoreObserver = new IntersectionObserver(
				(entries) => {
					const isIntersecting = entries[0].isIntersecting;

					if (isIntersecting && !this.isLoading && this.videos.pagination.currentPage < this.videos.pagination.lastPage) {
						this.loadMoreVideos();
					}
				},
				{
					rootMargin: '200px',
					threshold:  0.1,
				}
			);

			if (this.$refs.loadMoreTrigger) {
				this.loadMoreObserver.observe(this.$refs.loadMoreTrigger);
			}
		},
		setupPreviewObserver() {
			// Only setup preview observer for touch devices
			if (!this.isTouchDevice) {
				return;
			}

			// Clean up existing preview observer if it exists
			if (this.previewObserver) {
				this.previewObserver.disconnect();
			}

			this.previewObserver = new IntersectionObserver(
				() => {
					this.updateActiveVideo();
				},
				{
					rootMargin: '-80px 0px -80px 0px', // 80px margin from top and bottom
					threshold:  [0, 0.1, 0.5, 0.9, 1.0], // Multiple thresholds for better detection
				}
			);

			// Observe all event cards
			this.$nextTick(() => {
				const eventCardElements = document.querySelectorAll('[data-event-id]');
				eventCardElements.forEach((element) => {
					this.previewObserver.observe(element);
				});
			});
		},
		setupPreloadObserver() {
			// Clean up existing preload observer if it exists
			if (this.preLoadObserver) {
				this.preLoadObserver.disconnect();
			}

			this.preLoadObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							const eventId = entry.target.getAttribute('data-event-id');
							const eventCardRef = this.$refs[`eventCard-${eventId}`];
							if (eventCardRef && eventCardRef[0]) {
								eventCardRef[0].loadHlsUrl();
							}
						}
					});
				},
				{
					root:       null, // viewport
					rootMargin: '600px 0px 600px 0px',
					threshold:  0,
				}
			);

			// Observe all event cards
			this.$nextTick(() => {
				const eventCardElements = document.querySelectorAll('[data-event-id]');
				eventCardElements.forEach((element) => {
					this.preLoadObserver.observe(element);
				});
			});
		},
		updateActiveVideo() {
			// Get all event card elements
			const eventCardElements = document.querySelectorAll('[data-event-id]');
			const qualifyingCards = [];

			// Find all cards that are fully visible with 80px margins from top and bottom
			eventCardElements.forEach((element) => {
				const rect = element.getBoundingClientRect();
				const isFullyVisible = rect.top >= 80 && rect.bottom <= window.innerHeight - 80;

				if (isFullyVisible) {
					qualifyingCards.push({
						element,
						rect,
						id: element.getAttribute('data-event-id'),
					});
				}
			});

			// Sort by top position (topmost first) and take the first one
			qualifyingCards.sort((a, b) => a.rect.top - b.rect.top);
			const newActiveId = qualifyingCards.length > 0 ? qualifyingCards[0].id : null;

			// Only update if it's different to avoid unnecessary reactivity
			if (this.activePreviewCardId !== newActiveId) {
				this.activePreviewCardId = newActiveId;
			}
		},
		async getVideos() {
			await this.fetchVideos(this.videos?.pagination?.currentPage || 1, false);
		},
		async loadMoreVideos() {
			await this.fetchVideos(this.videos.pagination.currentPage + 1, true);
		},
		async onFilterChange(value) {
			window.history.pushState({}, '', laroute.route('arena.feed', { filter: value }));
			this.filterValue = value;
			await this.getVideos();
			this.scrollToTop();
		},
		scrollToTop() {
			const targetScrollElement = document.querySelector('.js-feed');
			const rect = targetScrollElement.getBoundingClientRect();
			// only scroll to top if user has scrolled down
			if (rect.top < 0) {
				const targetPosition = targetScrollElement.getBoundingClientRect().top + window.pageYOffset - 200;
				window.scrollTo(0, targetPosition);
			}
		},
		openFollowSearchModal() {
			this.eventBus.$emit(`modal:${this.modalNameFollowSearch}`);
		},
		handleCloseFollowSearchModal() {
			this.getVideos();
		},
		checkActiveVideoPosition() {
			// If there's no active video, nothing to check
			if (!this.activePreviewCardId) {
				return;
			}

			// Find the currently active video element
			const activeElement = document.querySelector(`[data-event-id="${this.activePreviewCardId}"]`);
			if (!activeElement) {
				return;
			}

			// Check if the active video is still fully visible with 80px margins from top and bottom
			const rect = activeElement.getBoundingClientRect();
			const isStillFullyVisible = rect.top >= 80 && rect.bottom <= window.innerHeight - 80;

			// If the active video is no longer fully visible, stop it and find a new one
			if (!isStillFullyVisible) {
				this.updateActiveVideo();
			}
		},
		handleShare(event) {
			this.shareNative({
				text: event.name,
				url:  event.url,
			});

			TrackingService.trackEvent(this.event.id, AnalyticsEvent.EVENT_SHARE);
		},
		async handleSave(event) {
			await this.bookmarkEvent({ event });
			storage.setCacheTs();
			this.loadProfile({ forceReload: true });
		},
		async handleUnsave(event) {
			await this.unbookmarkEvent({ event });
			storage.setCacheTs();
			this.loadProfile({ forceReload: true });

			if (this.filterValue === FILTERS.SAVED) {
				this.getVideos();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.feed {
	position: relative;

	@include size(md-up) {
		margin-bottom: $sp7;
	}
}

.feed__filter-container {
	@include z-index(arena-feed-chips-nav);
	position: sticky;
	top: 48px;

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 50%;
		width: 100vw;
		height: 100%;
		background-color: var(--color-body-background);
		transform: translateX(-50%);
		z-index: -1;
	}

	@include size(md-up) {
		&::after {
			display: none;
		}
	}
}

.feed__filter {
	position: sticky;
	top: 80px;
	margin-bottom: $sp4;
	padding-top: $sp4;
	padding-left: $sp4;
	padding-right: $sp4;

	@include size(sm-up) {
		margin-bottom: $sp5;
		padding-top: $sp5;
		padding-left: 0;
		padding-right: 0;
	}

	@include size(md-up) {
		padding-top: 0;
	}
}

.feed__cards {
	transition: opacity $trans-time-fast;
	opacity: 1;

	&.-loading {
		opacity: 0.5;
	}
}

.feed__loading-spinner {
	position: absolute;
	display: flex;
	align-items: center;
	top: 50%;
	left: 50%;
	transform: translateX(-50%) translateY(-50%);
	margin-bottom: $sp5;
}

.feed__load-more-trigger {
	height: 1px;
	width: 100%;
}

.feed__following-empty-state {
	padding-top: $sp5;
	padding-bottom: $sp6;
	border-top: none;
	border-bottom: none;
	margin-bottom: $sp5;

	@include size(sm-up) {
		padding-top: $sp6;
		padding-bottom: $sp7;
		margin-bottom: $sp7;
	}
}
</style>
